/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * https://github.com/facebook/lexical/blob/main/packages/lexical-playground/src/nodes/ImageNode.tsx
 *
 */

import {
  $createParagraphNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  ElementFormatType,
  GridSelection,
  LexicalNode,
  NodeKey,
  NodeSelection,
  ParagraphNode,
  RangeSelection,
  Spread,
} from "lexical";

import "./ImageNode.css";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  DecoratorBlockNode,
  SerializedDecoratorBlockNode,
} from "@lexical/react/LexicalDecoratorBlockNode";
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import { mergeRegister } from "@lexical/utils";
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
} from "lexical";
import {
  MouseEvent,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { ReactComponent as IconOpenInFull } from "./IconOpenInFull.svg";
import { ReactComponent as IconCloseFullscreen } from "./IconCloseFullscreen.svg";
import { app } from "initialize";

export interface ImagePayload {
  key?: NodeKey;
  src: string;
  size: ImageSizeType;
}

const imageCache = new Set<string>();

function useSuspenseImage(src: string) {
  if (imageCache.has(src)) {
    throw new Promise(async (resolve, reject) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        imageCache.add(src);
        resolve(null);
      };
      img.onerror = reject;
    });
  }
}

const isProjectImageUrl = (url: string, projectId: string): boolean => {
  return url.startsWith(
    `https://firebasestorage.googleapis.com/v0/b/${app.options.storageBucket}/o/projects%2F${projectId}%2Fimages%2F`
  );
};

const convertImageElement =
  (projectId: string) =>
  (domNode: Node): null | DOMConversionOutput => {
    if (domNode instanceof HTMLImageElement) {
      const { src } = domNode;
      if (isProjectImageUrl(src, projectId)) {
        const node = $createImageNode({ src, size: "large" });
        return { node };
      }
    }
    return null;
  };

function LazyImage({
  imageRef,
  src,
  size,
  focused,
  draggable,
}: {
  imageRef: { current: null | HTMLImageElement };
  src: string;
  size: ImageSizeType;
  focused: boolean;
  draggable: boolean;
}) {
  useSuspenseImage(src);

  return (
    <img
      className="ImageNode__LazyImage_root"
      src={src}
      alt=""
      ref={imageRef}
      draggable="false"
      data-size={size}
      data-focused={focused}
      data-draggable={draggable}
    />
  );
}

function ImageComponent({
  src,
  size,
  nodeKey,
}: {
  nodeKey: NodeKey;
  src: string;
  size: ImageSizeType;
}): JSX.Element {
  const imageRef = useRef(null);
  const [isSelected, setSelected, clearSelection] =
    useLexicalNodeSelection(nodeKey);
  const [editor] = useLexicalComposerContext();
  const [selection, setSelection] = useState<
    RangeSelection | NodeSelection | GridSelection | null
  >(null);

  const onDelete = useCallback(
    (payload: KeyboardEvent) => {
      if (isSelected && $isNodeSelection($getSelection())) {
        const event: KeyboardEvent = payload;
        event.preventDefault();
        const node = $getNodeByKey(nodeKey);
        if ($isImageNode(node)) {
          node.remove();
        }
        setSelected(false);
      }
      return false;
    },
    [isSelected, nodeKey, setSelected]
  );

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        setSelection(editorState.read(() => $getSelection()));
      }),
      editor.registerCommand<MouseEvent>(
        CLICK_COMMAND,
        (payload) => {
          const event = payload;

          if (event.target === imageRef.current) {
            if (!event.shiftKey) {
              clearSelection();
            }
            setSelected(!isSelected);
            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        onDelete,
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        onDelete,
        COMMAND_PRIORITY_LOW
      )
    );
  }, [clearSelection, editor, isSelected, nodeKey, onDelete, setSelected]);

  const onChangeSize = useCallback(
    (size: ImageSizeType) => {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if ($isImageNode(node)) {
          node.setSize(size);
          setSelected(true);
        }
      });
    },
    [editor, nodeKey, setSelected]
  );

  const draggable = isSelected && $isNodeSelection(selection);
  const isFocused = isSelected;

  return (
    <Suspense fallback={null}>
      <div className="ImageNode__ImageComponent_root" draggable={draggable}>
        <LazyImage
          src={src}
          size={size}
          imageRef={imageRef}
          focused={isFocused}
          draggable={draggable}
        />
        {size === "small" ? (
          <IconOpenInFull
            className="ImageNode__ImageComponent_button"
            onClick={() => onChangeSize("large")}
          />
        ) : (
          <IconCloseFullscreen
            className="ImageNode__ImageComponent_button"
            onClick={() => onChangeSize("small")}
          />
        )}
      </div>
    </Suspense>
  );
}

type ImageSizeType = "small" | "large";

export type SerializedImageNode = Spread<
  {
    src: string;
    size: ImageSizeType;
    type: "image";
    version: 1;
  },
  SerializedDecoratorBlockNode
>;

export class ImageNode extends DecoratorBlockNode {
  __src: string;
  __size: ImageSizeType;

  private static __projectId = "";

  static getType(): string {
    return "image";
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__src, node.__size, node.__format, node.__key);
  }

  static importJSON(
    serializedNode: SerializedImageNode
  ): ImageNode | ParagraphNode {
    let { src, size } = serializedNode;
    if (!isProjectImageUrl(src, this.__projectId)) {
      return $createParagraphNode();
    }

    const node = $createImageNode({
      src,
      size,
    });
    return node;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("img");
    element.setAttribute("src", this.__src);
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (node: Node) => ({
        conversion: convertImageElement(this.__projectId),
        priority: 0,
      }),
    };
  }

  static setProjectId(id: string) {
    this.__projectId = id;
  }

  constructor(
    src: string,
    size: ImageSizeType,
    format?: ElementFormatType,
    key?: NodeKey
  ) {
    super(format, key);
    this.__src = src;
    this.__size = size;
  }

  exportJSON(): SerializedImageNode {
    return {
      ...super.exportJSON(),
      src: this.getSrc(),
      size: this.getSize(),
      type: "image",
      version: 1,
    };
  }

  setSize(size: ImageSizeType) {
    const writable = this.getWritable();
    writable.__size = size;
  }

  // View

  updateDOM(): false {
    return false;
  }

  getSrc(): string {
    return this.__src;
  }

  getSize(): ImageSizeType {
    return this.__size;
  }

  decorate(): JSX.Element {
    return (
      <ImageComponent
        src={this.__src}
        nodeKey={this.getKey()}
        size={this.getSize()}
      />
    );
  }
}

export function $createImageNode({ src, size, key }: ImagePayload): ImageNode {
  return new ImageNode(src, size, undefined, key);
}

export function $isImageNode(
  node: LexicalNode | null | undefined
): node is ImageNode {
  return node instanceof ImageNode;
}
